import { Component, Input, OnChanges, SimpleChanges, ViewChild, Output, EventEmitter } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { RouterModule } from '@angular/router';
import { Feature } from 'geojson';

export interface CatchRow {
  catchId: string;
  fish_type_full_name: string;
  fish_type_display_color: string;
  time: string;
  temperature: number | null;
  water_temperature: number | null;
  water_temperature_trend: string | null;
  tide_cycle: string | null;
  wind_speed: string | null;
  wind_direction: string | null;
  short_forecast: string | null;
  lat: number;
  lng: number;
  has_user_notes: boolean;
  analysis_generated_at: string | null;
}

@Component({
  selector: 'app-catch-table',
  templateUrl: './catch-table.component.html',
  styleUrls: ['./catch-table.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, DatePipe, MatTableModule, MatSortModule]
})
export class CatchTableComponent implements OnChanges {
  @Input() features: Feature[] = [];
  /** Optional comma separated string of catch IDs so the Catch Detail view can "Next/Prev" them */
  @Input() listParam: string = '';

  dataSource = new MatTableDataSource<CatchRow>();
  displayedColumns = ['time', 'fish_type', 'tide', 'temperature', 'water_temperature', 'wind', 'forecast', 'notes', 'analysis_generated_at'];

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    if (sort) {
      this.dataSource.sort = sort;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['features']) {
      this.updateTable();
    }
  }

  private updateTable(): void {
    const rows: CatchRow[] = (this.features || []).map((f, i) => {
      const props = f.properties ?? {};
      const coords = f.geometry.type === 'Point' ? (f.geometry as GeoJSON.Point).coordinates : [0, 0];
      return {
        catchId: f.id !== undefined ? String(f.id) : (props['time'] ?? String(i)),
        fish_type_full_name: props['fish_type_full_name'] ?? 'Unknown',
        fish_type_display_color: props['fish_type_display_color'] ?? '#ffffff',
        time: props['time'] ?? '',
        temperature: props['temperature'] ?? null,
        water_temperature: props['water_temperature'] ?? null,
        water_temperature_trend: props['water_temperature_trend'] ?? null,
        tide_cycle: props['tide_cycle'] ?? null,
        wind_speed: props['wind_speed'] ?? null,
        wind_direction: props['wind_direction'] ?? null,
        short_forecast: props['short_forecast'] ?? null,
        lat: coords[1],
        lng: coords[0],
        has_user_notes: props['has_user_notes'] ?? false,
        analysis_generated_at: props['analysis_generated_at'] ?? null,
      };
    });

    this.dataSource.data = rows;
    
    // sortingDataAccessor supports dynamic custom logic instead of just straightforward props
    this.dataSource.sortingDataAccessor = (item, prop) => {
      if (prop === 'time') return item.time ? new Date(item.time).valueOf() : -Infinity;
      if (prop === 'fish_type') return item.fish_type_full_name;
      if (prop === 'tide') return item.tide_cycle ?? '';
      if (prop === 'temperature') return item.temperature ?? -Infinity;
      if (prop === 'water_temperature') return item.water_temperature ?? -Infinity;
      // Combine wind factors into an arbitrary comparative sort (speed then direction string compare)
      if (prop === 'wind') return `${item.wind_speed ?? '0'} ${item.wind_direction ?? ''}`;
      if (prop === 'forecast') return item.short_forecast ?? '';
      if (prop === 'notes') return item.has_user_notes ? 1 : 0;
      if (prop === 'analysis_generated_at') return item.analysis_generated_at ? new Date(item.analysis_generated_at).valueOf() : -Infinity;
      return '';
    };
  }
}
